import React, { useEffect, useRef } from "react";
import { Bell, Loader2, RefreshCw } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchNotifications,
    markAllNotificationsRead,
    markNotificationRead,
} from "../../store/notificationsSlice";
import NotificationItem from "./NotificationItem";

const NOTIFICATION_PAGE_LIMIT = 50;

const getMetaStringValue = (meta, key) =>
    typeof meta?.[key] === "string" && meta[key].trim()
        ? meta[key].trim()
        : null;

const getNotificationActionContext = (notification) => {
    const meta =
        notification?.meta &&
        typeof notification.meta === "object" &&
        !Array.isArray(notification.meta)
            ? notification.meta
            : {};
    const route = getMetaStringValue(meta, "route");
    const resultsUrl = getMetaStringValue(meta, "results_url");
    const actionType = getMetaStringValue(meta, "action_type");

    return {
        actionType,
        route,
        resultsUrl,
        isInformationalOnly:
            actionType === "system_alert" || (!route && !resultsUrl),
    };
};

const NotificationDropdown = ({
    className = "",
    onNotificationClick,
    autoFetch = true,
}) => {
    const dispatch = useDispatch();
    const requestedFeedOnMountRef = useRef(false);
    const {
        items,
        unreadCount,
        feedStatus,
        feedError,
        feedUnreadOnly,
        markReadStatusById,
        markAllReadStatus,
    } = useSelector((state) => state.notifications);

    const notifications = Array.isArray(items) ? items : [];
    const hasNotifications = notifications.length > 0;
    const hasUnread = Number(unreadCount) > 0;
    const isInitialLoading = feedStatus === "loading" && !hasNotifications;
    const isFeedLoading = feedStatus === "loading";
    const isMarkingAllRead = markAllReadStatus === "loading";
    const showInitialError = feedStatus === "failed" && !hasNotifications;
    const showInlineError = feedStatus === "failed" && hasNotifications;

    useEffect(() => {
        if (!autoFetch || requestedFeedOnMountRef.current) return;

        requestedFeedOnMountRef.current = true;

        if (feedStatus === "loading") return;

        dispatch(
            fetchNotifications({
                limit: NOTIFICATION_PAGE_LIMIT,
                unreadOnly: feedUnreadOnly,
            }),
        );
    }, [autoFetch, dispatch, feedStatus, feedUnreadOnly]);

    const handleRetry = () => {
        dispatch(
            fetchNotifications({
                limit: NOTIFICATION_PAGE_LIMIT,
                unreadOnly: feedUnreadOnly,
            }),
        );
    };

    const handleUnreadToggle = () => {
        if (isFeedLoading) return;

        dispatch(
            fetchNotifications({
                limit: NOTIFICATION_PAGE_LIMIT,
                unreadOnly: !feedUnreadOnly,
            }),
        );
    };

    const handleMarkAllRead = () => {
        if (!hasUnread || isMarkingAllRead) return;

        dispatch(markAllNotificationsRead());
    };

    const handleNotificationClick = (notification) => {
        if (!notification?.id) return;

        const actionContext = getNotificationActionContext(notification);
        const isMarkingRead =
            markReadStatusById?.[notification.id] === "loading";

        if (notification.is_read !== true && !isMarkingRead) {
            dispatch(markNotificationRead(notification.id));
        }

        onNotificationClick?.(notification, actionContext);
    };

    return (
        <section
            aria-label="Notifications"
            className={`app-dropdown-enter fixed left-3 right-3 top-16 z-[90] mt-2 flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-3 ${className}`}
            style={{
                width: "min(calc(100vw - 1.5rem), 24rem)",
                maxHeight: "min(34rem, calc(100vh - 5rem))",
            }}
        >
            <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-100 bg-indigo-50/60 px-4 py-3">
                <div className="min-w-0">
                    <h2 className="text-sm font-bold text-slate-950">
                        Notifications
                    </h2>
                    <p className="mt-0.5 text-xs font-medium text-slate-500">
                        {hasUnread
                            ? `${unreadCount} unread`
                            : "You're all caught up"}
                    </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    <button
                        type="button"
                        onClick={handleUnreadToggle}
                        disabled={isFeedLoading}
                        aria-pressed={feedUnreadOnly}
                        className={`inline-flex items-center justify-center rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${
                            feedUnreadOnly
                                ? "border-indigo-200 bg-indigo-600 text-white hover:bg-indigo-700"
                                : "border-indigo-100 bg-white text-indigo-700 hover:bg-indigo-50"
                        }`}
                    >
                        Unread
                    </button>

                    {hasUnread && (
                        <button
                            type="button"
                            onClick={handleMarkAllRead}
                            disabled={isMarkingAllRead}
                            className="inline-flex items-center justify-center rounded-full border border-indigo-100 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isMarkingAllRead
                                ? "Marking..."
                                : "Mark all as read"}
                        </button>
                    )}
                </div>
            </header>

            <div className="product-scrollbar min-h-0 flex-1 overflow-y-auto">
                {isInitialLoading && (
                    <div className="flex min-h-48 flex-col items-center justify-center px-5 py-8 text-center">
                        <Loader2
                            size={22}
                            className="animate-spin text-indigo-600"
                            aria-hidden="true"
                        />
                        <p className="mt-3 text-sm font-semibold text-slate-700">
                            Loading notifications...
                        </p>
                    </div>
                )}

                {showInitialError && (
                    <div className="flex min-h-48 flex-col items-center justify-center px-5 py-8 text-center">
                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                            <RefreshCw size={19} aria-hidden="true" />
                        </span>
                        <p className="mt-3 text-sm font-semibold text-slate-900">
                            Could not load notifications.
                        </p>
                        <p className="mt-1 text-xs font-medium leading-5 text-slate-500">
                            {feedError || "Please try again."}
                        </p>
                        <button
                            type="button"
                            onClick={handleRetry}
                            className="mt-4 rounded-full bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {!isInitialLoading && !showInitialError && !hasNotifications && (
                    <div className="flex min-h-48 flex-col items-center justify-center px-5 py-8 text-center">
                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                            <Bell size={19} aria-hidden="true" />
                        </span>
                        <p className="mt-3 text-sm font-semibold text-slate-900">
                            {feedUnreadOnly
                                ? "No unread notifications."
                                : "No notifications yet."}
                        </p>
                    </div>
                )}

                {showInlineError && (
                    <div className="border-b border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                        {feedError || "Could not refresh notifications."}
                    </div>
                )}

                {hasNotifications && (
                    <div>
                        {notifications.map((notification) => (
                            <NotificationItem
                                key={notification.id}
                                notification={notification}
                                onClick={handleNotificationClick}
                                isLoading={
                                    markReadStatusById?.[notification.id] ===
                                    "loading"
                                }
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default NotificationDropdown;
